'use client';

import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import { cva } from 'class-variance-authority';
import { MouseEvent, MutableRefObject, useCallback, type ReactNode } from 'react';
import { ChevronDown, Close } from '../../../icons';
import { cn } from '../../../lib/utils';
import { useFieldContext } from '../../field/field';
import { Icon } from '../../icon/icon';
import { useComboboxAnchorContext } from './combobox-context';

// ── Chips (multi-select wrapper) ──────────────────────────────────

const comboboxChipsVariants = cva(
  cn(
    'flex min-h-[44px] flex-wrap items-center gap-1.5 rounded-lg px-2.5 py-1.5',
    'typo-base text-space outline-none',
    'transition-[background-color,box-shadow,border] duration-150',
    'ring-0 ring-indigo-crush',
    'focus-within:bg-white focus-within:ring-2',
  ),
  {
    variants: {
      variant: {
        default: cn('bg-space-04', 'group-hover/combobox-chips:not-focus-within:bg-space-08'),
        white: cn(
          'border border-space-40 bg-white',
          'group-hover/combobox-chips:not-focus-within:bg-space-04',
          'focus-within:border-transparent',
        ),
      },
      error: {
        true: 'border-transparent bg-white ring-2 ring-errors',
        false: '',
      },
      disabled: {
        true: 'bg-space-04 text-space opacity-40',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      error: false,
      disabled: false,
    },
  },
);

export type ComboboxChipsProps = ComboboxPrimitive.Chips.Props & {
  variant?: 'default' | 'white';
  error?: boolean;
  disabled?: boolean;
  className?: string;
  classNames?: {
    wrapper?: string;
  };
  /** Show a chevron toggle on the right. @default false */
  showChevron?: boolean;
  /** Show a clear-all (×) button. @default false */
  showClear?: boolean;
  /** Ref forwarded to the outer wrapper — pass to ComboboxContent's `anchor` so the dropdown matches the full chips width. */
  anchorRef?: MutableRefObject<HTMLDivElement | null>;
};

/**
 * Multi-select container that renders chips for each selected value.
 *
 * **Note:** Unlike `ComboboxInput`, this component does not support a left icon
 * because the container grows vertically as chips wrap, making a fixed-position
 * icon impractical.
 */
export const ComboboxChips = ({
  variant = 'default',
  error: errorProp,
  disabled: disabledProp,
  className,
  classNames,
  showChevron = false,
  showClear = false,
  anchorRef: anchorRefProp,
  ...props
}: ComboboxChipsProps) => {
  const anchorContext = useComboboxAnchorContext();

  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (anchorRefProp) anchorRefProp.current = node;
      if (anchorContext) anchorContext.current = node;
    },
    [anchorRefProp, anchorContext],
  );

  const fieldContext = useFieldContext();
  const error = errorProp ?? fieldContext?.error ?? false;
  const disabled = disabledProp ?? fieldContext?.disabled ?? false;
  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    // If the click landed on the container padding (not on a chip or button),
    // focus the inline input so the whole rectangle feels clickable.
    const target = e.target as HTMLElement;
    if (target.closest('[data-slot=combobox-chip], button')) return;
    const input = e.currentTarget.querySelector<HTMLInputElement>(
      '[data-slot=combobox-chips-input]',
    );
    input?.focus();
  };

  const rightPadding = showClear && showChevron ? 'pr-16' : showClear || showChevron ? 'pr-9' : '';

  return (
    <div
      ref={setRef}
      className={cn(
        'group/combobox-chips relative cursor-text',
        disabled && 'pointer-events-none',
        classNames?.wrapper,
      )}
      onClick={handleClick}>
      <ComboboxPrimitive.Chips
        data-slot="combobox-chips"
        className={cn(comboboxChipsVariants({ variant, error, disabled }), rightPadding, className)}
        {...props}
      />

      {showClear && (
        <ComboboxPrimitive.Clear
          data-slot="combobox-chips-clear"
          disabled={disabled}
          className={cn(
            'absolute top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-lg',
            'text-space-60 transition-colors hover:not-disabled:text-space disabled:cursor-not-allowed',
            'focus-visible:ring-[3px] focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:outline-none',
            showChevron ? 'right-7' : 'right-1',
          )}>
          <Icon icon={Close} className="size-4" />
        </ComboboxPrimitive.Clear>
      )}

      {showChevron && (
        <ComboboxPrimitive.Trigger
          data-slot="combobox-chips-trigger"
          disabled={disabled}
          className={cn(
            'absolute top-1/2 right-3 -translate-y-1/2 text-space-60',
            'transition-transform duration-150',
            'disabled:cursor-not-allowed',
          )}>
          <Icon icon={ChevronDown} className="size-4" />
        </ComboboxPrimitive.Trigger>
      )}
    </div>
  );
};

// ── Chip (individual tag) ─────────────────────────────────────────

export type ComboboxChipProps = ComboboxPrimitive.Chip.Props & {
  showRemove?: boolean;
  className?: string;
  children?: ReactNode;
};

export const ComboboxChip = ({
  className,
  children,
  showRemove = true,
  ...props
}: ComboboxChipProps) => (
  <ComboboxPrimitive.Chip
    data-slot="combobox-chip"
    className={cn(
      'flex h-7 w-fit items-center justify-center gap-1 rounded-md bg-indigo-crush-10 px-2 text-sm whitespace-nowrap text-space',
      'has-disabled:pointer-events-none has-disabled:opacity-40',
      showRemove && 'pr-1',
      className,
    )}
    {...props}>
    {children}
    {showRemove && (
      <ComboboxPrimitive.ChipRemove
        data-slot="combobox-chip-remove"
        className={cn(
          'flex size-5 items-center justify-center rounded text-space-60 transition-colors',
          'hover:text-space',
        )}>
        <Icon icon={Close} className="size-3" />
      </ComboboxPrimitive.ChipRemove>
    )}
  </ComboboxPrimitive.Chip>
);

// ── Chips Input (inline input inside chips container) ─────────────

export const ComboboxChipsInput = ({ className, ...props }: ComboboxPrimitive.Input.Props) => (
  <ComboboxPrimitive.Input
    data-slot="combobox-chips-input"
    className={cn(
      'min-w-16 flex-1 bg-transparent outline-none placeholder:text-space-60',
      className,
    )}
    {...props}
  />
);
